import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useSalveazaTargetareSetare } from "@/hooks/api/mutate/useSalveazaTargetareSetare";
import { useGetTargetareSetari } from "@/hooks/api/query/useGetTargetareSetari";
import { useToast } from "@/hooks/use-toast";
import type { TargetareSetareCheieEditabila } from "@/types/targetare";
import { formatDataOra } from "@/utils/targetareFormatting";
import { HeroPagina } from "@/pages/AdministrareTargetare/components/HeroPagina";

function valoareLaText(v: unknown): string {
  if (v === null || v === undefined) return "–";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function BooleanField({
  cheie,
  eticheta,
  descriere,
  valoareCurenta,
}: {
  cheie: TargetareSetareCheieEditabila;
  eticheta: string;
  descriere: string;
  valoareCurenta: boolean;
}) {
  const { toast } = useToast();
  const salveaza = useSalveazaTargetareSetare();
  return (
    <div className="flex items-center justify-between gap-4 border-b py-3 last:border-b-0">
      <div>
        <p className="font-medium">{eticheta}</p>
        <p className="text-xs text-muted-foreground">{descriere}</p>
      </div>
      <Switch
        checked={valoareCurenta}
        disabled={salveaza.isPending}
        onCheckedChange={(v) =>
          salveaza.mutate(
            { cheie, valoare: v },
            {
              onSuccess: () =>
                toast({
                  title: `${eticheta}: ${v ? "activat" : "dezactivat"}`,
                }),
              onError: (e) =>
                toast({
                  title: "Salvarea a eșuat",
                  description: e.message,
                  variant: "destructive",
                }),
            }
          )
        }
      />
    </div>
  );
}

function NumberField({
  cheie,
  eticheta,
  descriere,
  valoareCurenta,
  min,
  max,
  step,
}: {
  cheie: TargetareSetareCheieEditabila;
  eticheta: string;
  descriere: string;
  valoareCurenta: number;
  min?: number;
  max?: number;
  step?: number;
}) {
  const { toast } = useToast();
  const salveaza = useSalveazaTargetareSetare();
  const [text, setText] = useState(String(valoareCurenta));

  const salveazaValoarea = () => {
    const numar = Number(text.replace(",", "."));
    if (!Number.isFinite(numar)) {
      toast({
        title: "Valoare invalidă",
        description: `${eticheta} trebuie să fie un număr.`,
        variant: "destructive",
      });
      return;
    }
    salveaza.mutate(
      { cheie, valoare: numar },
      {
        onSuccess: () => toast({ title: `${eticheta} actualizat` }),
        onError: (e) => {
          toast({
            title: "Salvarea a eșuat",
            description: e.message,
            variant: "destructive",
          });
          setText(String(valoareCurenta));
        },
      }
    );
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b py-3 last:border-b-0">
      <div>
        <p className="font-medium">{eticheta}</p>
        <p className="text-xs text-muted-foreground">{descriere}</p>
      </div>
      <div className="flex items-center gap-2">
        <Input
          className="w-28 font-mono"
          type="number"
          min={min}
          max={max}
          step={step ?? 1}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={salveaza.isPending || text === String(valoareCurenta)}
          onClick={salveazaValoarea}
        >
          Salvează
        </Button>
      </div>
    </div>
  );
}

function EnumField({
  cheie,
  eticheta,
  descriere,
  valoareCurenta,
  optiuni,
}: {
  cheie: TargetareSetareCheieEditabila;
  eticheta: string;
  descriere: string;
  valoareCurenta: string;
  optiuni: { valoare: string; eticheta: string }[];
}) {
  const { toast } = useToast();
  const salveaza = useSalveazaTargetareSetare();
  return (
    <div className="flex items-center justify-between gap-4 border-b py-3 last:border-b-0">
      <div>
        <p className="font-medium">{eticheta}</p>
        <p className="text-xs text-muted-foreground">{descriere}</p>
      </div>
      <Select
        value={valoareCurenta}
        disabled={salveaza.isPending}
        onValueChange={(v) =>
          salveaza.mutate(
            { cheie, valoare: v },
            {
              onSuccess: () => toast({ title: `${eticheta} actualizat` }),
              onError: (e) =>
                toast({
                  title: "Salvarea a eșuat",
                  description: e.message,
                  variant: "destructive",
                }),
            }
          )
        }
      >
        <SelectTrigger className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {optiuni.map((o) => (
            <SelectItem key={o.valoare} value={o.valoare}>
              {o.eticheta}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function Setari() {
  const setari = useGetTargetareSetari();
  const date = setari.data;
  const cauta = (cheie: string) =>
    date?.setari.find((s) => s.cheie === cheie) ?? null;

  const targetareActiv = cauta("targetare_activ");
  const anafTtl = cauta("anaf_live_ttl_zile");
  const anafVerificare = cauta("anaf_verificare");
  const pragSelectie = cauta("prag_selectie");
  const cursRonEur = cauta("curs_ron_eur");

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu="Setări"
        descriere={
          <>
            Cheile simple din{" "}
            <code className="font-mono">targetare.setare</code> se editează
            aici; fiecare modificare adaugă un rând nou (istoric complet, nimic
            nu se suprascrie). Restul parametrilor (praguri de dimensiune, regex
            sector public, reguli de acționariat) se editează doar prin
            migrație.
          </>
        }
        isLoading={setari.isLoading}
        coloane={0}
        tiles={[]}
      />

      {setari.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Setările nu au putut fi încărcate: {setari.error.message}
        </p>
      )}

      <div className="rounded-md border px-4">
        {setari.isLoading ? (
          <div className="space-y-3 py-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            <BooleanField
              cheie="targetare_activ"
              eticheta="Targetare activă"
              descriere="Comută citirile pe CUI în formularul de notificare pe modelul targetare (RPC-urile publice)."
              valoareCurenta={targetareActiv?.valoare === true}
            />
            <NumberField
              cheie="anaf_live_ttl_zile"
              eticheta="TTL cache ANAF live (zile)"
              descriere="Cât timp e valabil un răspuns ANAF live din cache înainte de o nouă interogare."
              valoareCurenta={Number(anafTtl?.valoare ?? 30)}
              min={1}
              step={1}
            />
            <EnumField
              cheie="anaf_verificare"
              eticheta="Verificare ANAF"
              descriere="Automat la fiecare interogare sau doar la cererea utilizatorului."
              valoareCurenta={String(anafVerificare?.valoare ?? "auto")}
              optiuni={[
                { valoare: "auto", eticheta: "Automat" },
                { valoare: "la_cerere", eticheta: "La cerere" },
              ]}
            />
            <NumberField
              cheie="prag_selectie"
              eticheta="Prag de selecție (rating %)"
              descriere="Ratingul minim de încredere pentru ca o încadrare să intre în targetare."
              valoareCurenta={Number(pragSelectie?.valoare ?? 70)}
              min={0}
              max={100}
              step={1}
            />
            <NumberField
              cheie="curs_ron_eur"
              eticheta="Curs RON/EUR (implicit)"
              descriere="Folosit când nu există un curs BNR specific anului bilanțului de mai jos."
              valoareCurenta={Number(cursRonEur?.valoare ?? 5)}
              min={0}
              step={0.0001}
            />
          </>
        )}
      </div>

      <div className="rounded-md border">
        <p className="border-b px-3 py-2 text-sm font-medium">
          Cursul BNR pe ani (citire)
        </p>
        {setari.isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-full" />
          </div>
        ) : (date?.curs_bnr ?? []).length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            Niciun curs BNR înregistrat.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 font-medium">An</th>
                <th className="px-3 py-1.5 font-medium">Curs</th>
                <th className="px-3 py-1.5 font-medium">Data</th>
                <th className="px-3 py-1.5 font-medium">Sursă</th>
              </tr>
            </thead>
            <tbody>
              {(date?.curs_bnr ?? []).map((r) => (
                <tr key={r.an} className="border-t">
                  <td className="px-3 py-1.5 font-mono">{r.an}</td>
                  <td className="px-3 py-1.5 tabular-nums">{r.curs}</td>
                  <td className="px-3 py-1.5 font-mono">{r.data}</td>
                  <td className="px-3 py-1.5">{r.sursa}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-md border">
        <p className="border-b px-3 py-2 text-sm font-medium">
          Toate setările (citire)
        </p>
        {setari.isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-full" />
          </div>
        ) : (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/60 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 font-medium">Cheie</th>
                  <th className="px-3 py-1.5 font-medium">Valoare</th>
                  <th className="px-3 py-1.5 font-medium">Valabil de la</th>
                </tr>
              </thead>
              <tbody>
                {(date?.setari ?? []).map((s) => (
                  <tr key={s.cheie} className="border-t align-top">
                    <td className="whitespace-nowrap px-3 py-1 font-mono">
                      {s.cheie}
                    </td>
                    <td className="max-w-[420px] break-words px-3 py-1">
                      {valoareLaText(s.valoare)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 font-mono">
                      {formatDataOra(s.valabil_de_la)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
